import {
	ATHENA_BILLING_PROVIDER_NOT_CONFIGURED,
	AthenaBillingProviderError,
} from "../../../errors.ts";
import type {
	BillingProviderConfigMap,
	MollieBillingProviderConfig,
} from "../../../providers/types.ts";
import type { ResolvedBillingProviderConnection } from "../../../reconciliation/types.ts";
import { createConfiguredProviderBinding } from "./binding.ts";
import { createBillingProviderExecutionContext } from "./execution-context.ts";
import { normalizeBillingProviderName } from "./registry.ts";
import type {
	BillingProviderExecutionContext,
	PersistedBillingProviderBinding,
} from "./types.ts";

export function defaultBillingCredentialReference(
	provider: string,
): string {
	return `providers.${normalizeBillingProviderName(provider)}`;
}

export function parseBillingCredentialReference(raw: string): {
	accountId?: string;
	provider: "mollie" | "stripe";
} {
	const trimmed = raw.trim();
	if (trimmed.length === 0) {
		throw new AthenaBillingProviderError({
			code: ATHENA_BILLING_PROVIDER_NOT_CONFIGURED,
			message: "Billing connection credential_reference is empty.",
		});
	}
	const body = trimmed.startsWith("providers.")
		? trimmed.slice("providers.".length)
		: trimmed;
	const colon = body.indexOf(":");
	const providerPart = (
		colon === -1 ? body : body.slice(0, colon)
	).toLowerCase();
	const accountId =
		colon === -1 ? undefined : body.slice(colon + 1).trim();
	if (providerPart !== "mollie" && providerPart !== "stripe") {
		throw new AthenaBillingProviderError({
			code: ATHENA_BILLING_PROVIDER_NOT_CONFIGURED,
			message: `Billing credential_reference "${trimmed}" is not a mollie or stripe slot.`,
		});
	}
	if (colon !== -1 && (accountId == null || accountId.length === 0)) {
		throw new AthenaBillingProviderError({
			code: ATHENA_BILLING_PROVIDER_NOT_CONFIGURED,
			message: `Billing credential_reference "${trimmed}" is missing an account slot.`,
		});
	}
	return accountId != null
		? { accountId, provider: providerPart }
		: { provider: providerPart };
}

export function mollieConfigForCredentialReference(
	configured: BillingProviderConfigMap | undefined,
	reference: string,
): MollieBillingProviderConfig | undefined {
	const parsed = parseBillingCredentialReference(reference);
	if (parsed.provider !== "mollie") {
		return undefined;
	}
	if (parsed.accountId == null) {
		return configured?.mollie;
	}
	return configured?.mollieAccounts?.[parsed.accountId];
}

export function firstConfiguredMollieProvider(
	configured?: BillingProviderConfigMap,
): MollieBillingProviderConfig | undefined {
	if (configured?.mollie != null) {
		return configured.mollie;
	}
	const accounts = configured?.mollieAccounts;
	if (accounts == null) {
		return undefined;
	}
	for (const slot of Object.values(accounts)) {
		if (slot != null) {
			return slot;
		}
	}
	return undefined;
}

export function createPersistedProviderBinding(input: {
	configuredProviders?: BillingProviderConfigMap;
	connectionId: string;
	credentialReference: string;
	provider: string;
}): PersistedBillingProviderBinding {
	const parsed = parseBillingCredentialReference(input.credentialReference);
	const connectionProvider = normalizeBillingProviderName(input.provider);
	if (parsed.provider !== connectionProvider) {
		throw new AthenaBillingProviderError({
			code: ATHENA_BILLING_PROVIDER_NOT_CONFIGURED,
			message: `Connection provider "${connectionProvider}" does not match credential_reference "${input.credentialReference}".`,
		});
	}
	const slotMap = slotConfigMap(
		parsed.provider,
		parsed.accountId,
		input.configuredProviders,
		input.credentialReference,
	);
	const configured = createConfiguredProviderBinding({
		configuredProviders: slotMap,
		provider: parsed.provider,
	});
	return {
		connectionId: input.connectionId,
		credentialReference: input.credentialReference,
		credentials: configured.credentials,
		kind: "connection",
		provider: configured.provider,
		providerConfig: configured.providerConfig,
	};
}

export function createConnectionProviderExecutionContext(input: {
	configuredProviders?: BillingProviderConfigMap;
	connection: ResolvedBillingProviderConnection;
	ingress?: { classicWebhookUrl?: string };
	operationScope?: "organization" | "profile" | "automatic";
}): BillingProviderExecutionContext {
	const binding = createPersistedProviderBinding({
		configuredProviders: input.configuredProviders,
		connectionId: input.connection.id,
		credentialReference: input.connection.credentialReference,
		provider: input.connection.provider,
	});
	return createBillingProviderExecutionContext({
		binding,
		...(input.ingress ? { ingress: input.ingress } : {}),
		...(input.operationScope != null
			? { operationScope: input.operationScope }
			: {}),
		testMode: input.connection.testMode,
	});
}

function slotConfigMap(
	provider: "mollie" | "stripe",
	accountId: string | undefined,
	configured: BillingProviderConfigMap | undefined,
	reference: string,
): BillingProviderConfigMap {
	if (provider === "mollie") {
		const mollie =
			accountId == null
				? configured?.mollie
				: configured?.mollieAccounts?.[accountId];
		if (mollie == null) {
			throw missingSlot(reference);
		}
		return { mollie };
	}
	const stripe =
		accountId == null
			? configured?.stripe
			: configured?.stripeAccounts?.[accountId];
	if (stripe == null) {
		throw missingSlot(reference);
	}
	return { stripe };
}

function missingSlot(reference: string): AthenaBillingProviderError {
	return new AthenaBillingProviderError({
		code: ATHENA_BILLING_PROVIDER_NOT_CONFIGURED,
		message: `No billing provider config is registered for credential_reference "${reference}".`,
	});
}